import React from 'react';
import {
  AppBar,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Pagination,
  Toolbar,
  Typography,
  useMediaQuery,
  type SxProps,
  type Theme
} from '@mui/material';
import { MedicalSuppliesService } from '../hooks';
import { notifications, extractApiErrorMessage } from '../utils';
import { useTranslation } from 'react-i18next';
import { Loader } from '.';
import { LoaderSize } from '../constants/constants';
import { DiseaseViewModel } from '../models/injection';
import CloseIcon from '@mui/icons-material/Close';
import { V4_OUTLINED_DEFAULT, V4_SMALL_SCREEN } from '../theme/v4Compat';

interface DiseaseSelectModalProps {
  disease: DiseaseViewModel | null;
  onChange: (supply: DiseaseViewModel | null) => void;
  selectorId?: string;
  disableAny?: boolean;
  disabled?: boolean;
  /** Extra styles for the trigger button. */
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
}

const DiseaseSelectModal = React.forwardRef((props: DiseaseSelectModalProps, ref) => {
  const { disease, onChange, selectorId, disableAny, sx, disabled, fullWidth } = props;
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);

  const medicalSuppliesService = new MedicalSuppliesService();

  const [diseases, setDiseases] = React.useState([] as DiseaseViewModel[]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const [text, setText] = React.useState('');

  React.useImperativeHandle(ref, () => ({
    openModal() {
      setOpen(true);
    }
  }));

  // pagination
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };
  // * pagination

  const displayCount = 10;

  const loadDiseases = async () => {
    setIsBusy(true);

    medicalSuppliesService
      .getFarmDiseases(page, displayCount)
      .then((result) => {
        setDiseases(result.data);
        setTotal(result.total);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleSelectorClick = () => {
    setOpen(true);
  };

  React.useEffect(() => {
    if (open && !loaded) {
      loadDiseases();
    }
  }, [open, loaded]);

  React.useEffect(() => {
    if (!disease) {
      setTextAny();
    } else {
      setTextByDisease(disease);
    }
  }, [disease]);

  const handleClose = () => {
    setOpen(false);
  };

  const setTextByDisease = (disease: DiseaseViewModel) => {
    setText(disease.name);
  };

  const setTextAny = () => {
    setText(t('f-disease-any'));
  };

  const handleSelectClick = (disease: DiseaseViewModel) => {
    setTextByDisease(disease);
    setOpen(false);
    onChange(disease);
  };

  const handleSelectAny = () => {
    setOpen(false);
    setTextAny();
    onChange(null);
  };

  return (
    <>
      <Button
        size="medium"
        key={selectorId || 'supplySelectModalBtn'}
        fullWidth={fullWidth}
        variant="outlined"
        color="inherit"
        sx={[V4_OUTLINED_DEFAULT, ...(Array.isArray(sx) ? sx : [sx])]}
        disabled={disabled}
        endIcon={<Icon>{'sick'}</Icon>}
        onClick={handleSelectorClick}
      >
        {text}
      </Button>

      <Dialog
        onClose={handleClose}
        aria-labelledby="disease-select-title"
        fullWidth
        maxWidth="sm"
        open={open}
        fullScreen={fullScreen}
      >
        {fullScreen ? (
          <AppBar position="relative">
            <Toolbar>
              <div className="flex w-full justify-between">
                <div className="flex items-center">
                  <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                    <CloseIcon />
                  </IconButton>
                  <Typography variant="h6">{t('f-disease-select-mod')}</Typography>
                </div>
              </div>
            </Toolbar>
          </AppBar>
        ) : (
          <DialogTitle id="disease-select-title">{t('f-disease-select-mod')}</DialogTitle>
        )}

        <DialogContent sx={{ p: 0 }}>
          <Divider />

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <div className="mr-4 mt-2 flex justify-end">
              <Typography>{t('f-search-results')}</Typography>
              <div className="ml-2">
                <Chip label={total} variant="outlined" size="small" id="totalChip" />
              </div>
            </div>
          )}

          {loaded && diseases && (
            <List dense>
              {diseases.map((disease) => (
                <ListItemButton onClick={() => handleSelectClick(disease)} key={disease.id}>
                  <ListItemAvatar>
                    <Avatar>{disease.name[0].toUpperCase() || '-'}</Avatar>
                  </ListItemAvatar>

                  <ListItemText primary={disease.name} />
                </ListItemButton>
              ))}

              <ListItem id="pagination-li" sx={{ justifyContent: 'center' }}>
                <Pagination
                  count={Math.floor(total / displayCount)}
                  size="small"
                  color="primary"
                  showFirstButton
                  showLastButton
                  page={page}
                  onChange={handleChangePage}
                />
              </ListItem>
            </List>
          )}
        </DialogContent>

        <DialogActions>
          {!fullScreen && (
            <Button onClick={handleClose} color="primary">
              {t('m-global-close')}
            </Button>
          )}
          <Button onClick={handleSelectAny} color="primary" disabled={disableAny}>
            {t('f-disease-any')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default DiseaseSelectModal;
